import { Button, Col } from "antd";
import { ReactNode } from "react";
import { EntityManager } from "../../components/entityManager";
import { enmControlType, enmPropertyType, IMetadata } from "../../components/entityManager/interfaces";
import { Core } from "../../components/infrastructure/core";
import { AndFilter, ComparisonFilter, IFilter } from "../../components/infrastructure/filtering";
import { enmEntityState, IFetchingInfo, IResultSet } from "../../components/infrastructure/models";
import SimpleMap from "../../components/map";
import { City } from "../city";
import { ICityProps } from "../city/interfaces";
import { EvaluatorDamageType } from "../evaluatorDamageType";
import { EvaluatorEquipment } from "../evaluatorEquipment";
import { Province } from "../province";
import { RepairCenterEquipment } from "../repairCenterEquipment";
import { UserRelation } from "../userRelation";
import { enmUserTypes } from "../userRelation/zobdehUser";
import { enmPersonalityTypes, ICenter, ICenterInitialData, ICenterProps, ICenterStates, personalityTypesOptions } from "./interfaces";

export class Center extends EntityManager<ICenter, ICenterProps, ICenterStates>{
    static initMetadata = (): IMetadata => {
        return {
            name: "Center",
            title: "مراکز",
            Properties: [
                {
                    title: "نوع مرکز",
                    dataIndex: "Personality",
                    propertyType: enmPropertyType.number,
                    controlType: enmControlType.combobox,
                    combobox: {
                        options: personalityTypesOptions
                    },
                    filterable: true,
                    width: 150,
                    rules: [{ required: true, message: 'لطفا نوع مرکز را تعیین نمایید' }]
                },
                {
                    title: "کد/شناسه ملی",
                    dataIndex: "NationalCode",
                    propertyType: enmPropertyType.string,
                    controlType: enmControlType.textbox,
                    filterable: true,
                    width: 150,
                    rules: [{ required: true, message: 'لطفا کد/شناسه ملی مرکز را وارد نمایید' }]
                },
                {
                    title: "کد مرکز",
                    dataIndex: "Code",
                    propertyType: enmPropertyType.number,
                    controlType: enmControlType.numberinput,
                    filterable: true,
                    rules: [{ required: true, message: 'لطفا کد مرکز را تعیین نمایید' }],
                    disabled: true,
                    width: 150
                },
                {
                    title: "نام شرکت",
                    dataIndex: "Name",
                    propertyType: enmPropertyType.string,
                    controlType: enmControlType.textbox,
                    filterable: true,
                    width: 150,
                    rulesFunc: (entity: ICenter) => {
                        if (entity.Personality === enmPersonalityTypes.Legal) {
                            return [{ required: true, message: 'لطفا نام مرکز را وارد نمایید' }];
                        }
                    },
                    visibility: (entity: ICenter) => {
                        return entity.Personality === enmPersonalityTypes.Legal;
                    },
                    showColumn: false
                },
                {
                    title: "نام",
                    dataIndex: "FirstName",
                    propertyType: enmPropertyType.string,
                    controlType: enmControlType.textbox,
                    filterable: true,
                    width: 150,
                    rulesFunc: (entity: ICenter) => {
                        if (entity.Personality === enmPersonalityTypes.Actual) {
                            return [{ required: true, message: 'لطفا نام مرکز را وارد نمایید' }];
                        }
                    },
                    visibility: (entity: ICenter) => {
                        return entity.Personality === enmPersonalityTypes.Actual;
                    },
                    showColumn: false
                },
                {
                    title: "نام خانوادگی",
                    dataIndex: "LastName",
                    propertyType: enmPropertyType.string,
                    controlType: enmControlType.textbox,
                    filterable: true,
                    width: 150,
                    rulesFunc: (entity: ICenter) => {
                        if (entity.Personality === enmPersonalityTypes.Actual) {
                            return [{ required: true, message: 'لطفا نام خانوادگی مرکز را وارد نمایید' }];
                        }
                    },
                    visibility: (entity: ICenter) => {
                        return entity.Personality === enmPersonalityTypes.Actual;
                    },
                    showColumn: false
                },
                {
                    title: "نام مرکز",
                    dataIndex: "FullName",
                    propertyType: enmPropertyType.string,
                    controlType: enmControlType.textbox,
                    filterable: false,
                    width: 200,
                    showControl: false,
                    sortable: false
                },
                {
                    title: "مرکز اصلی",
                    dataIndex: "ParentId",
                    propertyType: enmPropertyType.number,
                    controlType: enmControlType.lookup,
                    filterable: true,
                    width: 150,
                    lookup: {
                        lookupComponent: Center,
                        lookupProperty: "Parent",
                        lookupDisplayFields: ["Code", "FullName"]
                    }
                },
                {
                    title: "مرکز ارزیابی؟",
                    dataIndex: "IsEvaluator",
                    propertyType: enmPropertyType.boolean,
                    controlType: enmControlType.checkbox,
                    filterable: true,
                    width: 120
                },
                {
                    title: "مرکز تعمیرات؟",
                    dataIndex: "IsRepairCenter",
                    propertyType: enmPropertyType.boolean,
                    controlType: enmControlType.checkbox,
                    filterable: true,
                    width: 140
                },
                {
                    title: "بیمه گرهای مجاز",
                    dataIndex: "Insurers",
                    propertyType: enmPropertyType.number,
                    controlType: enmControlType.combobox,
                    combobox: {
                        optionsFunc: (sender: Center) => {
                            return sender.state.insurers.map(i => {
                                return { title: i.Name, value: i.Id };
                            });
                        },
                        mode: "multiple"
                    },
                    visibility: (entity: ICenter) => {
                        return entity.IsEvaluator;
                    },
                    filterable: false,
                    width: 150,
                    showColumn: false
                },
                {
                    title: "استان",
                    dataIndex: "ProvinceId",
                    propertyType: enmPropertyType.number,
                    controlType: enmControlType.lookup,
                    filterable: true,
                    width: 150,
                    rules: [{ required: true, message: 'لطفا استان را وارد نمایید' }],
                    lookup: {
                        lookupComponent: Province,
                        lookupProperty: "Province",
                        lookupDisplayFields: ["Name"],

                    }
                },
                {
                    title: "شهر",
                    dataIndex: "CityId",
                    propertyType: enmPropertyType.number,
                    controlType: enmControlType.lookup,
                    filterable: true,
                    width: 150,
                    rules: [{ required: true, message: 'لطفا شهر را وارد نمایید' }],
                    lookup: {
                        lookupComponent: City,
                        lookupProperty: "City",
                        lookupDisplayFields: ["Name"]
                    }
                },
                {
                    title: "آدرس",
                    dataIndex: "Address",
                    propertyType: enmPropertyType.string,
                    controlType: enmControlType.textbox,
                    filterable: true,
                    width: 150,
                    showColumn: false
                },
                {
                    title: "کد پستی",
                    dataIndex: "PostalCode",
                    propertyType: enmPropertyType.string,
                    controlType: enmControlType.textbox,
                    filterable: true,
                    width: 150,
                    showColumn: false,
                    rules: [{
                        validator: async (_, value: any) => {
                            if (!!value && value.length !== 10)
                                return Promise.reject(new Error('کد پستی باید 10 رقم باشد'));
                            return Promise.resolve();
                        }
                    }]
                },
                {
                    title: "تلفن",
                    dataIndex: "Phone",
                    propertyType: enmPropertyType.string,
                    controlType: enmControlType.numberinput,
                    filterable: true,
                    formatter: value => {
                        const formated = `${value}`.length ? `0${value}`.replace(/\D+/g, '') : "";
                        return formated.startsWith('09') ? formated : formated.replace(/(\d{3})(\d+)/, '$1-$2');
                    },
                    parser: value => {
                        const parsed = (value && value.length && value.startsWith('0') ? value.substring(1) : value)?.replace(/\\s?|(-*)/g, '') ?? "";
                        return parsed;
                    },
                    onKeyPress: (event, value) => {
                        if (!event.key.match(/\d/g) || `${value}`.length >= 10) {
                            event.preventDefault();
                        }
                    },
                    maxLength: 12,
                    width: 150,
                    showColumn: false
                },
                {
                    title: "نمابر",
                    dataIndex: "Fax",
                    propertyType: enmPropertyType.string,
                    controlType: enmControlType.textbox,
                    filterable: true,
                    width: 150,
                    showColumn: false
                },
                {
                    title: "تلفن همراه",
                    dataIndex: "Mobile",
                    propertyType: enmPropertyType.string,
                    controlType: enmControlType.numberinput,
                    formatter: value => {
                        const formated = `${value}`.length ? `0${value}`.replace(/\D+/g, '') : "";
                        return formated;
                    },
                    parser: value => {
                        const parsed = (value && value.length && value.startsWith('0') ? value.substring(1) : value) ?? "";
                        return parsed;
                    },
                    onKeyPress: (event, value) => {
                        if (!event.key.match(/\d/g) || `${value}`.length >= 10) {
                            event.preventDefault();
                        }
                    },
                    maxLength: 11,
                    filterable: true,
                    width: 150,
                    showColumn: false
                },
                {
                    title: "شماره شبا",
                    dataIndex: "ShebaNo",
                    propertyType: enmPropertyType.string,
                    controlType: enmControlType.textbox,
                    filterable: true,
                    width: 150,
                    showColumn: false
                },
                {
                    title: "نشانی الکترونیک",
                    dataIndex: "Email",
                    propertyType: enmPropertyType.string,
                    controlType: enmControlType.email,
                    filterable: true,
                    width: 150,
                    showColumn: false
                },
                {
                    title: "ساعت کار مرکز",
                    dataIndex: "WorkTime",
                    propertyType: enmPropertyType.string,
                    controlType: enmControlType.textbox,
                    filterable: false,
                    width: 150,
                    showColumn: false
                },
                {
                    title: "مجوز ارزیابی مجدد دارد؟",
                    dataIndex: "SecondEvaluationPermission",
                    propertyType: enmPropertyType.boolean,
                    controlType: enmControlType.checkbox,
                    filterable: true,
                    visibility: (entity: ICenter) => {
                        return entity.IsEvaluator;
                    },
                    width: 200
                },
                {
                    title: "فعال؟",
                    dataIndex: "IsActive",
                    propertyType: enmPropertyType.boolean,
                    controlType: enmControlType.checkbox,
                    filterable: true,
                    width: 100
                },
                {
                    title: "موقعیت مکانی",
                    dataIndex: "Location",
                    showColumn: false,
                    controlType: enmControlType.template,
                    filterable: false,
                    renderControl: (entity: ICenter) => {
                        return (
                            <SimpleMap
                                cityName={entity.City?.Name}
                                lat={entity.Latitude}
                                lng={entity.Longitude}
                                onChangeMarker={(lat, lng) => {
                                    entity.Latitude = lat;
                                    entity.Longitude = lng;
                                    if (entity.ModifiedProperties.indexOf("Latitude") === -1)
                                        entity.ModifiedProperties.push("Latitude");
                                    if (entity.ModifiedProperties.indexOf("Longitude") === -1)
                                        entity.ModifiedProperties.push("Longitude");
                                    if (entity.State !== enmEntityState.Added)
                                        entity.State = enmEntityState.Modified;
                                }} />
                        );
                    }
                }
            ]
        };
    }

    onInitializing() {
        this.searchable = true;

        this.searchArea = {
            columnsPerRow: 2,
            onFilterChanged: (property, _, form) => {
                if (property.dataIndex === "ProvinceId") {
                    form?.resetFields(["CityId"]);
                    form?.setFields([{ name: "CityId", value: null }]);
                }
            }
        };

        this.enableDeleteCommand = entity => entity.Id > 1;

        this.preparelookupProps = (propName, lookupProps: any, form, isSearchForm) => {
            if (propName === "CityId")
                (lookupProps as ICityProps).params = () => {
                    let province = isSearchForm && form ? form.getFieldValue("ProvinceId") : this.state.currentEntity?.Province;
                    if (isSearchForm && form)
                        return { province: province?.value };
                    else
                        return { province };
                };

            return lookupProps;
        };

        this.originalFilter = () => {
            let filter: IFilter | undefined;
            if (this.props.isEvaluator)
                filter = new AndFilter([
                    ComparisonFilter.Equal<ICenter>(c => c.IsEvaluator, true),
                    ComparisonFilter.Equal<ICenter>(c => c.IsActive, true)
                ]);

            if (this.props.isRepairCenter) {
                let tmp = new AndFilter([
                    ComparisonFilter.Equal<ICenter>(c => c.IsRepairCenter, true),
                    ComparisonFilter.Equal<ICenter>(c => c.IsActive, true)
                ]);
                filter = filter ? filter.Or(tmp) : tmp;
            }

            return filter;
        };

        this.onDataFetching = (fetchingInfo: IFetchingInfo) => {

            if (this.props.params) {
                const { provinceId, cityId } = this.props.params();
                if (provinceId) {
                    let tmp = ComparisonFilter.Equal<ICenter>(c => c.ProvinceId, provinceId);
                    fetchingInfo.Filter = fetchingInfo.Filter ? fetchingInfo.Filter.And(tmp) : tmp;
                }

                if (cityId) {
                    let tmp = ComparisonFilter.Equal<ICenter>(c => c.CityId, cityId);
                    fetchingInfo.Filter = fetchingInfo.Filter ? fetchingInfo.Filter.And(tmp) : tmp;
                }
            }
            return fetchingInfo;
        };

        //this.onDataFetched = result => {
        //    result && result.Data && result.Data.forEach(i => {
        //        if (i && i.Mobile && !i.Mobile.startsWith('0'))
        //            i.Mobile = '0' + i.Mobile;
        //    });
        //    return result;
        //}

        this.onLoaded = (initialData) => {
            this.setState({ insurers: (initialData as ICenterInitialData).Insurers });
        };

        this.onEntityView = (entity) => {
            entity.Insurers = entity.EvaluatorInsurers.map(e => e.InsurerId);
            //if (entity.Phone && !entity.Phone.startsWith('0'))
            //    entity.Phone = '0' + entity.Phone;
            //if (entity.Mobile && !entity.Mobile.startsWith('0'))
            //    entity.Mobile = '0' + entity.Mobile;
            return true;
        };

        this.onEntityEdit = (entity) => {
            entity.Insurers = entity.EvaluatorInsurers.map(e => e.InsurerId);
            //if (entity.Phone && !entity.Phone.startsWith('0'))
            //    entity.Phone = '0' + entity.Phone;
            //if (entity.Mobile && !entity.Mobile.startsWith('0'))
            //    entity.Mobile = '0' + entity.Mobile;
            return true;
        };

        this.onEntityDelete = (entity) => {
            entity.Insurers = entity.EvaluatorInsurers.map(e => e.InsurerId);
            return true;
        };

        this.onEntityChanged = async (entity, propertyName, form) => {
            if (propertyName === "ProvinceId") {
                form.resetFields(["CityId"]);
                form.setFields([{ name: "CityId", value: null }]);
                entity.City = undefined;
                entity.CityId = 0;
            }
            if (propertyName === "CityId") {
                if (!entity.Province) {
                    form.resetFields(["ProvinceId"]);
                    form.setFields([{ name: "ProvinceId", value: entity.City?.Province }]);
                    entity.Province = entity.City?.Province;
                    entity.ProvinceId = entity.City?.ParentId ?? 0;
                }
            }
            if (propertyName === "ParentId") {
                await this.callApi({
                    url: "center/getnextcode", data: { parentCode: entity.Parent?.Code }, onSuccess: (result: IResultSet<any>) => {
                        if (result.ValidationState.Successful) {
                            entity.Code = result.Data;
                        }
                        form.setFields([{ name: "Code", value: entity.Code }]);
                    }
                });
            }
        };

        this.onEntitySaving = (entity) => {
            entity.EvaluatorInsurers.forEach(e => {
                if (!entity.Insurers.find(i => i === e.InsurerId))
                    e.State = enmEntityState.Deleted;
            });
            entity.Insurers.forEach(e => {
                if (!entity.EvaluatorInsurers.find(i => i.InsurerId === e))
                    entity.EvaluatorInsurers.push({ RowGuid: Core.newId(), State: enmEntityState.Added, EvaluatorId: this.state.currentEntity?.Id!, InsurerId: e } as any);
            });
            if (entity.ModifiedProperties.find(e => e === "Insurers")) {
                let index = entity.ModifiedProperties.indexOf("Insurers");
                entity.ModifiedProperties.splice(index, 1);
                entity.ModifiedProperties.push("EvaluatorInsurers");
            }
            return true;
        };

        this.onEntityAdded = async (entity) => {
            entity.Personality = enmPersonalityTypes.Actual;
            entity.EvaluatorInsurers = [];
            entity.Insurers = [];
            entity.MainCenterId = 1;
            entity.ModifiedProperties.push('MainCenterId');
            await this.callApi({
                url: "center/getnextcode", data: { ParentCode: null }, onSuccess: (result: IResultSet<any>) => {
                    entity.Code = result.Data ?? 0;
                    entity.ModifiedProperties.push('Code');
                }
            });
            return true;
        };

        this.renderingToolbar = () => {
            let result: ReactNode[] = [];
            let userEvaluatorAccess = this.initialData?.AccessList["UserCenter"];
            let evaluatorEquipmentAccess = this.initialData?.AccessList["EvaluatorEquipment"];
            let evaluatorDamageTypeAccess = this.initialData?.AccessList["EvaluatorDamageType"];
            let repairCenterEquipmentAccess = this.initialData?.AccessList["RepairCenterEquipment"];

            if (userEvaluatorAccess === true) {
                result.push(
                    <Col key="Users" style={{ marginLeft: 10 }}>
                        <Button type="default" onClick={this.onUsersClick}>کاربران مرکز</Button>
                    </Col>
                );
            }

            if (evaluatorEquipmentAccess === true && this.selectedEntity?.IsEvaluator) {
                result.push(
                    <Col key="EvaluatorEquipments" style={{ marginLeft: 10 }}>
                        <Button type="default" onClick={this.onEvaluatorEquipmentClick}>تجهیزات مجاز ارزیابی</Button>
                    </Col>
                );
            }

            if (evaluatorDamageTypeAccess === true && this.selectedEntity?.IsEvaluator) {
                result.push(
                    <Col key="EvaluatorDamageTypes" style={{ marginLeft: 10 }}>
                        <Button type="default" onClick={this.onEvaluatorDamageTypeClick}>نوع خسارت مجاز ارزیابی</Button>
                    </Col>
                );
            }

            if (repairCenterEquipmentAccess === true && this.selectedEntity?.IsRepairCenter) {
                result.push(
                    <Col key="RepairCenterEquipments" style={{ marginLeft: 10 }}>
                        <Button type="default" onClick={this.onRepairCenterEquipmentClick}>تجهیزات مجاز تعمیر</Button>
                    </Col>
                );
            }

            return result.length ? (<> {result} </>) : (<></>);
        };

        this.getDefaultState = () => {
            return { insurers: [] };
        };
    }
    onUsersClick = () => {
        this.addLookupForm("UserCenter",
            <UserRelation
                key="UserCenter"
                title="کاربران مرکز"
                metadataName="UserCenter"
                invokePath={this.metadata.title}
                isChildComponent={true}
                onClose={this.onLookupClosed}
                userType={enmUserTypes.Spc}
                params={() => {
                    return { sourceId: this.selectedEntity?.Id, description: this.selectedEntity?.FullName ?? "" }
                }}
            />);
    }
    onEvaluatorEquipmentClick = () => {
        this.addLookupForm("EvaluatorEquipment",
            <EvaluatorEquipment
                key="EvaluatorEquipment"
                invokePath={this.metadata.title}
                isChildComponent={true}
                onClose={this.onLookupClosed}
                params={() => {
                    return { evaluator: this.selectedEntity }
                }}
            />);
    }
    onEvaluatorDamageTypeClick = () => {
        this.addLookupForm("EvaluatorDamageType",
            <EvaluatorDamageType
                key="EvaluatorDamageType"
                invokePath={this.metadata.title}
                isChildComponent={true}
                onClose={this.onLookupClosed}
                params={() => {
                    return { evaluator: this.selectedEntity }
                }}
            />);
    }
    onRepairCenterEquipmentClick = () => {
        this.addLookupForm("RepairCenterEquipment",
            <RepairCenterEquipment
                key="RepairCenterEquipment"
                invokePath={this.metadata.title}
                isChildComponent={true}
                onClose={this.onLookupClosed}
                params={() => {
                    return { repairCenter: this.selectedEntity }
                }}
            />);
    }
}